import type { ChangeEvent, FormEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../../config';
import { TEST_URLS } from '../constants';

export interface CreateFormData {
  name: string;
  description: string;
}

export interface MleConfig {
  keyId: string;
  serverCertificate: string;
  privateKey: string;
}

export interface ProfileFormData {
  name: string;
  description: string;
  portal: string;
  authType: string;
  authConfig: Record<string, any>;
  mleConfig: MleConfig;
}

const INITIAL_CREATE_FORM: CreateFormData = { name: '', description: '' };

const INITIAL_PROFILE_FORM: ProfileFormData = {
  name: '',
  description: '',
  portal: '',
  authType: 'mtls',
  authConfig: {},
  mleConfig: { keyId: '', serverCertificate: '', privateKey: '' },
};

export const useProxyProfiles = () => {
  const [profiles, setProfiles] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [viewingProfile, setViewingProfile] = useState<any>(null);
  const [testResults, setTestResults] = useState<Record<string, any>>({});
  const [authTestResults, setAuthTestResults] = useState<Record<string, any>>({});
  const [mleTestResults, setMleTestResults] = useState<Record<string, any>>({});
  const [testUrl, setTestUrl] = useState('https://sandbox.api.visa.com/');
  const [createFormData, setCreateFormData] = useState<CreateFormData>(INITIAL_CREATE_FORM);
  const [profileFormData, setProfileFormData] = useState<ProfileFormData>(INITIAL_PROFILE_FORM);

  const loadProxyStatus = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/proxy/load-config`);
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.config) {
          if (data.config.testUrl) setTestUrl(data.config.testUrl);
        }
      }
    } catch (error) {
      console.error('Failed to load proxy status:', error);
    }
  }, []);

  const fetchProfiles = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/proxy-profiles`);
      const data = await response.json();

      if (data.success) {
        const filteredProfiles = data.profiles.filter(
          (profile: any) => profile.portal !== 'direct' && profile.id !== 'direct-default',
        );
        setProfiles(filteredProfiles);
      } else {
        toast.error('Failed to load proxy profiles');
      }
    } catch (error) {
      console.error('Error fetching profiles:', error);
      toast.error('Failed to load proxy profiles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProxyStatus();
    fetchProfiles();
  }, [fetchProfiles, loadProxyStatus]);

  const saveTestUrl = async (): Promise<void> => {
    try {
      const loadResponse = await fetch(`${API_BASE_URL}/api/proxy/load-config`);
      const loadData = await loadResponse.json();

      if (!loadData.success || !loadData.config) {
        toast.error('Failed to load current config');
        return;
      }

      const updatedConfig = { ...loadData.config, testUrl };

      const saveResponse = await fetch(`${API_BASE_URL}/api/proxy/save-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedConfig),
      });

      const saveData = await saveResponse.json();
      if (saveData.success) {
        toast.success('Test URL saved successfully');
      } else {
        toast.error('Failed to save Test URL');
      }
    } catch (error) {
      console.error('Error saving test URL:', error);
      toast.error('Failed to save Test URL');
    }
  };

  const handleOpenCreateModal = (): void => {
    setCreateFormData(INITIAL_CREATE_FORM);
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = (): void => {
    setShowCreateModal(false);
  };

  const handleOpenProfile = (profile: any): void => {
    setViewingProfile(profile);

    const mode = profile.portal || '';
    setTestUrl(TEST_URLS[mode] || '');

    setProfileFormData({
      name: profile.name,
      description: profile.description || '',
      portal: mode,
      authType: profile.authType === 'direct' ? 'mtls' : profile.authType,
      authConfig: profile.authConfig || {},
      mleConfig: profile.mleConfig || { keyId: '', serverCertificate: '', privateKey: '' },
    });
  };

  const handleCloseProfile = (): void => {
    setViewingProfile(null);
  };

  const handleCreateProfile = async (e: FormEvent): Promise<void> => {
    e.preventDefault();

    if (!createFormData.name) {
      toast.error('Profile name is required');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/proxy-profiles`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: createFormData.name,
          description: createFormData.description,
          portal: '',
          authType: 'mtls',
          authConfig: {},
          mleConfig: { keyId: '', serverCertificate: '', privateKey: '' },
        }),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Profile created successfully');
        handleCloseCreateModal();
        fetchProfiles();
      } else {
        toast.error(data.error || 'Failed to create profile');
      }
    } catch (error) {
      console.error('Error creating profile:', error);
      toast.error('Failed to create profile');
    }
  };

  const handleUpdateProfile = async (e: FormEvent): Promise<void> => {
    e.preventDefault();

    if (!profileFormData.name || !profileFormData.authType) {
      toast.error('Name and auth type are required');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/proxy-profiles/${viewingProfile.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileFormData),
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Profile updated successfully');
        fetchProfiles();
        setViewingProfile({ ...viewingProfile, ...profileFormData });
      } else {
        toast.error(data.error || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error('Failed to update profile');
    }
  };

  const handleDelete = async (profileId: string, profileName: string): Promise<void> => {
    if (!window.confirm(`Are you sure you want to delete "${profileName}"?`)) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/proxy-profiles/${profileId}`, {
        method: 'DELETE',
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Profile deleted successfully');
        fetchProfiles();
      } else {
        toast.error(data.error || 'Failed to delete profile');
      }
    } catch (error) {
      console.error('Error deleting profile:', error);
      toast.error('Failed to delete profile');
    }
  };

  const handleTestProfile = async (profile: any): Promise<void> => {
    setTestResults((prev) => ({ ...prev, [profile.id]: { loading: true } }));

    try {
      const mode = profile.portal || '';

      if (!mode) {
        toast.error('Mode is not configured. Please select a mode (VDP, CyberSource, or Authorize.Net) first.');
        setTestResults((prev) => ({
          ...prev,
          [profile.id]: {
            success: false,
            message: 'Mode not configured',
            error: 'Please select a mode in profile settings',
          },
        }));
        return;
      }

      if (mode === 'authorize') {
        const authConfigured = profile.authConfig?.apiLoginId && profile.authConfig?.transactionKey;
        if (!authConfigured) {
          toast.error('Authentication is not configured. Please configure API Login ID and Transaction Key first.');
          setTestResults((prev) => ({
            ...prev,
            [profile.id]: {
              success: false,
              message: 'Authentication not configured',
              error: 'Please fill in API Login ID and Transaction Key',
            },
          }));
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/proxy/test-authorizenet`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            apiLoginId: profile.authConfig.apiLoginId,
            transactionKey: profile.authConfig.transactionKey,
            testUrl: TEST_URLS.authorize,
          }),
        });

        const data = await response.json();
        setTestResults((prev) => ({ ...prev, [profile.id]: data }));
        toast[data.success ? 'success' : 'error'](`${profile.name} test ${data.success ? 'successful' : 'failed'}`);
        return;
      }

      if (mode === 'cybersource') {
        const httpSigConfigured =
          profile.authType === 'http_signature' &&
          profile.authConfig?.merchantId &&
          profile.authConfig?.merchantKeyId &&
          profile.authConfig?.merchantSecretKey;

        const jwtConfigured =
          profile.authType === 'jwt' &&
          profile.authConfig?.merchantId &&
          profile.authConfig?.keyAlias &&
          profile.authConfig?.keyPassword &&
          profile.authConfig?.keyFileContent;

        if (!httpSigConfigured && !jwtConfigured) {
          toast.error(
            'Authentication is not configured. Please configure all required CyberSource authentication fields.',
          );
          setTestResults((prev) => ({
            ...prev,
            [profile.id]: {
              success: false,
              message: 'Authentication not configured',
              error: 'Please fill in all required authentication fields',
            },
          }));
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/proxy/test-cybersource`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            authType: profile.authType,
            authConfig: profile.authConfig,
            testUrl: TEST_URLS.cybersource,
          }),
        });

        const data = await response.json();
        setTestResults((prev) => ({ ...prev, [profile.id]: data }));
        toast[data.success ? 'success' : 'error'](`${profile.name} test ${data.success ? 'successful' : 'failed'}`);
        return;
      }

      if (mode === 'vdp') {
        const authConfigured =
          (profile.authType === 'mtls' &&
            profile.authConfig?.userId &&
            profile.authConfig?.cert &&
            profile.authConfig?.key) ||
          (profile.authType === 'xpay' && profile.authConfig?.api_key && profile.authConfig?.shared_secret);

        if (!authConfigured) {
          toast.error('Authentication is not configured. Please configure Auth settings first.');
          setTestResults((prev) => ({
            ...prev,
            [profile.id]: {
              success: false,
              message: 'Authentication not configured',
              error: 'Please fill in all required authentication fields',
            },
          }));
          return;
        }

        const mleConfigured =
          profile.mleConfig?.keyId && profile.mleConfig?.serverCertificate && profile.mleConfig?.privateKey;

        if (!mleConfigured) {
          toast.error('MLE is not configured. Please configure MLE settings first.');
          setTestResults((prev) => ({
            ...prev,
            [profile.id]: {
              success: false,
              message: 'MLE not configured',
              error: 'Please fill in Key ID, Server Certificate, and Private Key',
            },
          }));
          return;
        }

        const response = await fetch(`${API_BASE_URL}/api/proxy/test-mle`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            authType: profile.authType,
            authConfig: profile.authConfig,
            mleConfig: {
              key_id: profile.mleConfig.keyId,
              server_certificate: profile.mleConfig.serverCertificate,
              private_key: profile.mleConfig.privateKey,
            },
            testUrl: `${TEST_URLS.vdp}visadirect/fundstransfer/v1/pushfundstransactions`,
          }),
        });

        const data = await response.json();
        setTestResults((prev) => ({ ...prev, [profile.id]: data }));
        toast[data.success ? 'success' : 'error'](
          `${profile.name} test ${data.success ? 'successful (Auth + MLE)' : 'failed'}`,
        );
        return;
      }

      toast.warning('Test not implemented for this mode yet');
      setTestResults((prev) => ({ ...prev, [profile.id]: null }));
    } catch (error: any) {
      setTestResults((prev) => ({
        ...prev,
        [profile.id]: { success: false, message: 'Test failed', error: error.message },
      }));
      toast.error(`${profile.name} test failed`);
    }
  };

  const handleTestAuth = async (profile: any): Promise<void> => {
    setAuthTestResults((prev) => ({ ...prev, [profile.id]: { loading: true } }));

    try {
      const mode = profileFormData.portal || profile.portal || '';

      if (!mode) {
        toast.error('Mode is not configured. Please select a mode (VDP, CyberSource, or Authorize.Net) first.');
        setAuthTestResults((prev) => ({
          ...prev,
          [profile.id]: {
            success: false,
            message: 'Mode not configured',
            error: 'Please select a mode in profile settings',
          },
        }));
        return;
      }

      let endpoint = '';
      let body = {};

      if (mode === 'authorize') {
        endpoint = `${API_BASE_URL}/api/proxy/test-authorizenet`;
        body = {
          apiLoginId: profileFormData.authConfig.apiLoginId,
          transactionKey: profileFormData.authConfig.transactionKey,
          testUrl: testUrl || TEST_URLS.authorize,
        };
      } else if (mode === 'cybersource') {
        endpoint = `${API_BASE_URL}/api/proxy/test-cybersource`;
        body = {
          authType: profileFormData.authType,
          authConfig: profileFormData.authConfig,
          testUrl: testUrl || TEST_URLS.cybersource,
        };
      } else if (profileFormData.authType === 'mtls') {
        endpoint = `${API_BASE_URL}/api/proxy/test-mtls`;
        body = {
          ...profileFormData.authConfig,
          testUrl: `${testUrl}vdp/helloworld`,
        };
      } else if (profileFormData.authType === 'xpay') {
        endpoint = `${API_BASE_URL}/api/proxy/test-xpay`;
        body = {
          ...profileFormData.authConfig,
          testUrl: `${testUrl}vdp/helloworld`,
        };
      } else {
        toast.warning('Test not implemented for this auth type yet');
        setAuthTestResults((prev) => ({ ...prev, [profile.id]: null }));
        return;
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });

      const data = await response.json();
      setAuthTestResults((prev) => ({ ...prev, [profile.id]: data }));
      toast[data.success ? 'success' : 'error'](`Authentication test ${data.success ? 'successful' : 'failed'}`);
    } catch (error: any) {
      setAuthTestResults((prev) => ({
        ...prev,
        [profile.id]: { success: false, message: 'Authentication test failed', error: error.message },
      }));
      toast.error('Authentication test failed');
    }
  };

  const handleTestMle = async (profile: any): Promise<void> => {
    setMleTestResults((prev) => ({ ...prev, [profile.id]: { loading: true } }));

    try {
      if (!profileFormData.mleConfig.keyId) {
        toast.warning('Please enter Key ID first');
        setMleTestResults((prev) => ({ ...prev, [profile.id]: null }));
        return;
      }

      const response = await fetch(`${API_BASE_URL}/api/proxy/test-mle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          authType: profileFormData.authType,
          authConfig: profileFormData.authConfig,
          mleConfig: {
            key_id: profileFormData.mleConfig.keyId,
            server_certificate: profileFormData.mleConfig.serverCertificate,
            private_key: profileFormData.mleConfig.privateKey,
          },
          testUrl: `${testUrl}visadirect/fundstransfer/v1/pushfundstransactions`,
        }),
      });

      const data = await response.json();
      setMleTestResults((prev) => ({ ...prev, [profile.id]: data }));
      toast[data.success ? 'success' : 'error'](`MLE test ${data.success ? 'successful' : 'failed'}`);
    } catch (error: any) {
      setMleTestResults((prev) => ({
        ...prev,
        [profile.id]: { success: false, message: 'MLE test failed', error: error.message },
      }));
      toast.error('MLE test failed');
    }
  };

  const handleTextFileUpload = (target: 'authConfig' | 'mleConfig', field: string, file: File): void => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setProfileFormData((prev) => ({
        ...prev,
        [target]: { ...prev[target], [field]: e.target?.result as string },
      }));
      toast.success(`File uploaded for ${field}`);
    };
    reader.readAsText(file);
  };

  const handleFileUpload = (field: string, file: File): void => handleTextFileUpload('authConfig', field, file);
  const handleMleFileUpload = (field: string, file: File): void => handleTextFileUpload('mleConfig', field, file);

  const handlePortalChange = (newMode: string): void => {
    setProfileFormData((prev) => ({ ...prev, portal: newMode }));
    setTestUrl(TEST_URLS[newMode] || '');
  };

  const handleP12Upload = (e: ChangeEvent<HTMLInputElement>): void => {
    if (e.target.files?.[0]) {
      const file = e.target.files[0];
      const reader = new FileReader();
      reader.onload = (event) => {
        const base64 = (event.target?.result as string).split(',')[1];
        setProfileFormData((prev) => ({
          ...prev,
          authConfig: { ...prev.authConfig, keyFileContent: base64, keyFilename: file.name },
        }));
        toast.success(`P12 key file uploaded: ${file.name}`);
      };
      reader.readAsDataURL(file);
    }
  };

  return {
    profiles,
    loading,
    showCreateModal,
    viewingProfile,
    testResults,
    authTestResults,
    mleTestResults,
    testUrl,
    setTestUrl,
    createFormData,
    setCreateFormData,
    profileFormData,
    setProfileFormData,
    saveTestUrl,
    handleOpenCreateModal,
    handleCloseCreateModal,
    handleOpenProfile,
    handleCloseProfile,
    handleCreateProfile,
    handleUpdateProfile,
    handleDelete,
    handleTestProfile,
    handleTestAuth,
    handleTestMle,
    handleFileUpload,
    handleMleFileUpload,
    handlePortalChange,
    handleP12Upload,
  };
};
